import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon from "./Icon";
import { patchGuestForm, submitEventRegistration } from "../../store/events";
import { Button, GuestFormFields } from "./FormControls";
import {
  guestFields,
  formatEventDate,
  formatWindow,
  shopTitle,
} from "./nsdFields";
import "./events.css";

/**
 * Off-site community service events: guests register to take part, so there's no
 * slot picker and no vehicle. Location matters here in a way it doesn't for
 * Classic, since the event isn't at the shop.
 */
const CustomRegister = () => {
  const dispatch = useDispatch();
  const {
    event,
    guestForm: v,
    submitting,
    submitError,
  } = useSelector((state) => state.events);

  const set = (p) => dispatch(patchGuestForm(p));

  const [bookingSlow, setBookingSlow] = useState(false);
  useEffect(() => {
    if (!submitting) {
      setBookingSlow(false);
      return undefined;
    }
    const t = setTimeout(() => setBookingSlow(true), 8000);
    return () => clearTimeout(t);
  }, [submitting]);

  const alertRef = useRef(null);
  useEffect(() => {
    if (submitError) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [submitError]);

  const [touched, setTouched] = useState({});
  const onTouch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const fields = guestFields(event, v);
  const req = fields.filter((f) => f.required);

  const err = {};
  fields.forEach((f) => {
    const value = v[f.key];
    if (value && f.validate && !f.validate(value)) {
      err[f.key] = f.msg || "Please check this field.";
    } else if (touched[f.key] && f.required && !value) {
      err[f.key] = f.msg || "This field is required.";
    }
  });

  const formValid = req.every(
    (f) => v[f.key] && (!f.validate || f.validate(v[f.key]))
  );

  const submit = () => {
    if (!formValid) return;
    dispatch(submitEventRegistration());
  };

  return (
    <div className="ev-wrap">
      <div className="ev-eyebrow">Community Service Event</div>
      <h1 className="ev-h1">{event.name}</h1>
      <div className="ev-meta">
        <span className="ev-meta-item">
          <Icon name="calendar" color="var(--brand-legacy-green)" />{" "}
          {formatEventDate(event.eventDate)}
        </span>
        <span className="ev-meta-item">
          <Icon name="clock" color="var(--brand-legacy-green)" />{" "}
          {formatWindow(event.startTime, event.endTime)}
        </span>
        <span className="ev-meta-item">
          <Icon name="pin" color="var(--brand-legacy-green)" />{" "}
          {event.location || shopTitle(event.shop?.name)}
        </span>
      </div>

      {event.description && <p className="ev-lead">{event.description}</p>}
      {event.partner && (
        <p className="ev-lead ev-follow-up">
          Hosted with <strong>{event.partner}</strong>.
        </p>
      )}

      <h2 className="ev-head">Sign up to take part</h2>
      {submitError && (
        <div className="ev-conflict" ref={alertRef}>
          Something went wrong while signing you up. Please try again, or call{" "}
          {shopTitle(event.shop?.name) || "the shop"}
          {event.shop?.phone ? ` at ${event.shop.phone}` : ""} if it keeps
          happening.
        </div>
      )}
      <GuestFormFields
        event={event}
        v={v}
        set={set}
        err={err}
        onTouch={onTouch}
      />

      <div className="ev-actions">
        <Button
          kind="primary"
          icon="heart"
          onClick={submit}
          disabled={submitting || !formValid}
          busy={submitting}
        >
          {submitting ? "Signing you up…" : "Count me in"}
        </Button>
        <span className="ev-note">
          {submitting && bookingSlow
            ? "Still working. Please don't close this page or sign up again."
            : "We'll email you the details."}
        </span>
      </div>
    </div>
  );
};

export default CustomRegister;
